"use client";

import { useRef, useEffect, forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { useGoogleMaps } from "@/components/providers/googleMapsProvider";

interface AddressAutocompleteProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onChange" | "onBlur"
  > {
  onAddressSelect?: (address: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    coordinates?: { lat: number; lng: number };
  }) => void;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

// Type definitions for the new Google Maps API
interface PlaceSelectEvent extends Event {
  placePrediction: {
    toPlace(): google.maps.places.Place;
  };
}

// CSS to fix Google Maps autocomplete dropdown pointer events
const globalStyles = `
.pac-container {
  z-index: 10000 !important;
  pointer-events: auto !important;
}
gmp-place-autocomplete {
  width: 100%;
}
gmp-place-autocomplete > input {
  width: 100%;
  height: 2.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid hsl(var(--border));
  border-radius: calc(var(--radius) - 2px);
  background-color: hsl(var(--background));
  font-size: 0.875rem;
  transition: border-color;
}
gmp-place-autocomplete > input:focus {
  outline: none;
  border-color: hsl(var(--ring));
  box-shadow: 0 0 0 2px hsl(var(--ring) / 0.2);
}
gmp-place-autocomplete > input:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
`;

export const AddressAutocomplete = forwardRef<
  HTMLInputElement,
  AddressAutocompleteProps
>(({ onAddressSelect, className, value, onChange, onBlur, ...props }, ref) => {
  const { isLoaded, google } = useGoogleMaps();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const autocompleteElementRef =
    useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
  const inputElementRef = useRef<HTMLInputElement | null>(null);

  // Initialize autocomplete when Google Maps API is loaded
  useEffect(() => {
    // Only initialize if Google Maps is loaded and the container exists
    if (
      isLoaded &&
      google &&
      containerRef.current &&
      !autocompleteElementRef.current
    ) {
      try {
        // Create the new PlaceAutocompleteElement
        const placeAutocomplete =
          new google.maps.places.PlaceAutocompleteElement({
            types: ["address"],
          });

        placeAutocomplete.id = "place-autocomplete-input";

        // Set placeholder text and other properties
        const inputElement = placeAutocomplete.querySelector("input");
        if (inputElement) {
          inputElementRef.current = inputElement;

          inputElement.placeholder =
            props.placeholder || "Start typing your address...";
          inputElement.disabled = !isLoaded;

          // Apply specific props we care about safely
          if (props.required !== undefined) {
            inputElement.required = props.required;
          }
          if (props.autoComplete !== undefined) {
            inputElement.setAttribute("autocomplete", props.autoComplete);
          }
          if (props.name !== undefined) {
            inputElement.name = props.name;
          }
          if (props.id !== undefined) {
            inputElement.id = props.id;
          }

          // Set initial value
          if (value) {
            inputElement.value = value;
          }

          // Add input event listener to sync with form
          inputElement.addEventListener("input", (e) => {
            if (onChange) {
              const syntheticEvent = {
                target: e.target,
                currentTarget: e.target,
                type: "change",
                nativeEvent: e,
              } as React.ChangeEvent<HTMLInputElement>;
              onChange(syntheticEvent);
            }
          });

          // Add blur event listener for validation
          inputElement.addEventListener("blur", (e) => {
            if (onBlur) {
              const syntheticEvent = {
                target: e.target,
                currentTarget: e.target,
                type: "blur",
                nativeEvent: e,
              } as React.FocusEvent<HTMLInputElement>;
              onBlur(syntheticEvent);
            }
          });
        }

        // Add event listener for place selection using proper event listener interface
        placeAutocomplete.addEventListener(
          "gmp-select",
          async (event: Event) => {
            try {
              const selectEvent = event as PlaceSelectEvent;
              const place = selectEvent.placePrediction.toPlace();

              // Fetch the required fields
              await place.fetchFields({
                fields: ["addressComponents", "location", "displayName"],
              });

              // Extract address data from the new Place object
              let streetNumber = "";
              let route = "";
              let city = "";
              let state = "";
              let postalCode = "";
              let country = "";
              let unit = "";

              // Check for unit number in the display name
              const unitMatch = place.displayName?.match(/^(\d+)\/(\d+)/);
              if (unitMatch) {
                unit = `Unit ${unitMatch[1]}`;
                streetNumber = unitMatch[2];
              }

              // Extract address components using the correct Google Maps types
              place.addressComponents?.forEach(
                (component: google.maps.places.AddressComponent) => {
                  const type = component.types[0];

                  if (type === "street_number")
                    streetNumber = component.longText || "";
                  if (type === "route") route = component.longText || "";
                  if (type === "locality") city = component.longText || "";
                  if (type === "administrative_area_level_1")
                    state = component.shortText || "";
                  if (type === "postal_code")
                    postalCode = component.longText || "";
                  if (type === "country") country = component.shortText || "";
                }
              );

              // Prepare the address data
              const addressData = {
                address1: `${streetNumber} ${route}`.trim(),
                address2: unit || undefined,
                city,
                state,
                postalCode,
                country,
                coordinates: place.location
                  ? {
                      lat: place.location.lat(),
                      lng: place.location.lng(),
                    }
                  : undefined,
              };

              // Call the callback with the extracted address
              if (onAddressSelect) {
                onAddressSelect(addressData);
              }

              // Clear the autocomplete input after selection
              if (inputElementRef.current) {
                inputElementRef.current.value = "";
                if (onChange) {
                  const syntheticEvent = {
                    target: inputElementRef.current,
                    currentTarget: inputElementRef.current,
                  } as React.ChangeEvent<HTMLInputElement>;
                  onChange(syntheticEvent);
                }
              }
            } catch (error) {
              console.error("Error processing place selection:", error);
            }
          }
        );

        // Append to container
        containerRef.current.appendChild(placeAutocomplete);
        autocompleteElementRef.current = placeAutocomplete;
      } catch (error) {
        console.error("Error initializing PlaceAutocompleteElement:", error);
      }
    }
  }, [isLoaded, google, onAddressSelect, onChange, onBlur, props, value]);

  // Sync value changes from form to input
  useEffect(() => {
    if (inputElementRef.current && value !== undefined) {
      if (inputElementRef.current.value !== value) {
        inputElementRef.current.value = value;
      }
    }
  }, [value]);

  // Handle ref forwarding to the actual input element
  useEffect(() => {
    if (inputElementRef.current && ref) {
      if (typeof ref === "function") {
        ref(inputElementRef.current);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current =
          inputElementRef.current;
      }
    }
  }, [ref]);

  // Show loading state or the autocomplete element
  if (!isLoaded) {
    return (
      <Input
        ref={ref}
        type='text'
        placeholder='Loading address search...'
        disabled={true}
        className={className}
        value={value || ""}
        onChange={onChange}
        onBlur={onBlur}
        {...props}
      />
    );
  }

  return (
    <>
      <style jsx global>
        {globalStyles}
      </style>
      <div ref={containerRef} className={`w-full ${className || ""}`} />
    </>
  );
});

AddressAutocomplete.displayName = "AddressAutocomplete";
